import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const TABS = ['Overview','Tenants','Users','Plans','Feature Flags','Audit Logs','Settings'];
const GLOBAL_ROLES = ['admin','member','viewer','agent','client'];
const EMPTY_PLAN = { name: '', price_monthly: 0, price_yearly: 0, max_members: 5, max_projects: 10, max_api_keys: 5, max_monthly_calls: 10000, features: [] };

export default function SuperAdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('Overview');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  // Create modals
  const [modal, setModal] = useState(null); // 'user' | 'tenant' | 'editUser' | 'plan' | null
  const [plans, setPlans] = useState([]);
  const [userForm, setUserForm]     = useState({ name: '', email: '', password: '', role: 'member' });
  const [tenantForm, setTenantForm] = useState({ name: '', owner_email: '', owner_name: '', plan_id: 'plan_free' });
  const [createdNote, setCreatedNote] = useState(null); // one-time temp-credential reveal
  const [editUser, setEditUser] = useState(null);       // { id, name, role } being edited
  const [planForm, setPlanForm] = useState(EMPTY_PLAN); // plan create/edit form
  const [editingPlanId, setEditingPlanId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'Overview') {
        const [ov, growth] = await Promise.all([
          api.get('/platform/analytics/overview'),
          api.get('/platform/analytics/growth?days=30'),
        ]);
        setData({ overview: ov.data, growth: growth.data });
      } else if (tab === 'Tenants') {
        const res = await api.get('/platform/tenants?limit=50');
        setData({ tenants: res.data });
      } else if (tab === 'Users') {
        const res = await api.get('/platform/users?limit=50');
        setData({ users: res.data });
      } else if (tab === 'Plans') {
        const res = await api.get('/platform/plans');
        setData({ plans: res.data });
      } else if (tab === 'Feature Flags') {
        const res = await api.get('/platform/feature-flags');
        setData({ flags: res.data });
      } else if (tab === 'Audit Logs') {
        const res = await api.get('/platform/audit-logs?limit=50');
        setData({ logs: res.data });
      } else if (tab === 'Settings') {
        const res = await api.get('/platform/settings');
        setData({ settings: res.data });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load data');
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const suspendTenant = async (id) => {
    if (!window.confirm('Suspend this tenant?')) return;
    try { await api.post(`/platform/tenants/${id}/suspend`); toast.success('Tenant suspended'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const activateTenant = async (id) => {
    try { await api.post(`/platform/tenants/${id}/activate`); toast.success('Tenant activated'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  // Load plans once for the Create Tenant modal
  useEffect(() => { api.get('/plans').then(r => setPlans(r.data)).catch(() => {}); }, []);

  const errMsg = (err) => err.response?.data?.error || err.response?.data?.details?.[0]?.message || 'Failed';

  const createUser = async () => {
    if (!userForm.name.trim() || !userForm.email.trim() || userForm.password.length < 8) {
      toast.error('Name, email, and an 8+ char password are required'); return;
    }
    try {
      const res = await api.post('/platform/users', userForm);
      toast.success(`User created as ${res.data.role}`);
      setModal(null);
      setUserForm({ name: '', email: '', password: '', role: 'member' });
      if (tab === 'Users') load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const createTenant = async () => {
    if (!tenantForm.name.trim() || !tenantForm.owner_email.trim()) {
      toast.error('Company name and owner email are required'); return;
    }
    try {
      const res = await api.post('/platform/tenants', tenantForm);
      if (res.data.owner_temp_password) {
        setCreatedNote({ email: tenantForm.owner_email, temp_password: res.data.owner_temp_password });
      } else {
        toast.success('Tenant onboarded');
      }
      setModal(null);
      setTenantForm({ name: '', owner_email: '', owner_name: '', plan_id: 'plan_free' });
      if (tab === 'Tenants') load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // ── User management ──
  const openEditUser = (u) => { setEditUser({ id: u.id, name: u.name, role: u.role }); setModal('editUser'); };
  const saveUser = async () => {
    try {
      await api.put(`/platform/users/${editUser.id}`, { name: editUser.name, role: editUser.role });
      toast.success('User updated'); setModal(null); setEditUser(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const toggleUserStatus = async (u) => {
    const action = u.status === 'inactive' ? 'activate' : 'deactivate';
    if (action === 'deactivate' && !window.confirm(`Deactivate ${u.name}? They will be unable to log in.`)) return;
    try { await api.post(`/platform/users/${u.id}/${action}`); toast.success(`User ${action}d`); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // ── Plan management ──
  const openCreatePlan = () => { setEditingPlanId(null); setPlanForm(EMPTY_PLAN); setModal('plan'); };
  const openEditPlan = (p) => {
    setEditingPlanId(p.id);
    setPlanForm({
      name: p.name, price_monthly: p.price_monthly, price_yearly: p.price_yearly,
      max_members: p.max_members, max_projects: p.max_projects, max_api_keys: p.max_api_keys,
      max_monthly_calls: p.max_monthly_calls, features: p.features || [],
    });
    setModal('plan');
  };
  const savePlan = async () => {
    if (!planForm.name.trim()) { toast.error('Plan name is required'); return; }
    const payload = {
      ...planForm,
      price_monthly: Number(planForm.price_monthly), price_yearly: Number(planForm.price_yearly),
      max_members: Number(planForm.max_members), max_projects: Number(planForm.max_projects),
      max_api_keys: Number(planForm.max_api_keys), max_monthly_calls: Number(planForm.max_monthly_calls),
    };
    try {
      if (editingPlanId) { await api.put(`/platform/plans/${editingPlanId}`, payload); toast.success('Plan updated'); }
      else { await api.post('/platform/plans', payload); toast.success('Plan created'); }
      setModal(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const togglePlan = async (p) => {
    try { await api.post(`/platform/plans/${p.id}/toggle`, { is_active: !p.is_active }); toast.success(p.is_active ? 'Plan deactivated' : 'Plan activated'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const deletePlan = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try { await api.delete(`/platform/plans/${p.id}`); toast.success('Plan deleted'); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const S = { // inline styles
    page:   { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
    header: { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 56 },
    badge:  { background: '#f38ba833', color: '#f38ba8', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, letterSpacing: 1 },
    body:   { flex: 1, padding: 24 },
    tabs:   { display: 'flex', gap: 4, marginBottom: 24, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 4 },
    tab:    (active) => ({ padding: '7px 14px', borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: active ? 'var(--accent)' : 'transparent', color: active ? '#fff' : 'var(--text-2)' }),
    card:   { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 },
    grid4:  { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 },
    stat:   { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 },
    statVal:{ fontSize: 28, fontWeight: 700, marginBottom: 4 },
    statLbl:{ fontSize: 12, color: 'var(--text-3)' },
    tbl:    { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th:     { textAlign: 'left', padding: '8px 12px', color: 'var(--text-3)', borderBottom: '1px solid var(--border)', fontWeight: 500 },
    td:     { padding: '10px 12px', borderBottom: '1px solid var(--border)' },
  };

  const statusColor = (s) => s === 'active' ? '#a6e3a1' : s === 'suspended' ? '#f38ba8' : '#fab387';

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg,#f38ba8,#fab387)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>⚡</div>
        <span style={{ fontWeight: 700, fontSize: 14 }}>Platform Admin</span>
        <span style={S.badge}>SUPER ADMIN</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{user?.name}</span>
        <button onClick={() => { logout(); navigate('/login'); }} className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>Sign out</button>
      </div>

      <div style={S.body}>
        <div style={S.tabs}>
          {TABS.map(t => <button key={t} style={S.tab(tab === t)} onClick={() => setTab(t)}>{t}</button>)}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Loading…</div>
        ) : (
          <>
            {/* OVERVIEW */}
            {tab === 'Overview' && data.overview && (
              <>
                <div style={S.grid4}>
                  {[
                    { label: 'Total Tenants',  val: data.overview.total_tenants, icon: '🏢' },
                    { label: 'Active Tenants', val: data.overview.active_tenants, icon: '✅' },
                    { label: 'Total Users',    val: data.overview.total_users, icon: '👥' },
                    { label: 'Monthly Revenue', val: `$${Number(data.overview.mrr || 0).toLocaleString()}`, icon: '💰' },
                  ].map(m => (
                    <div key={m.label} style={S.stat}>
                      <div style={{ fontSize: 22, marginBottom: 8 }}>{m.icon}</div>
                      <div style={S.statVal}>{m.val}</div>
                      <div style={S.statLbl}>{m.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div style={S.card}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Plan Distribution</h3>
                    {(data.overview.plan_distribution || []).map(p => (
                      <div key={p.plan} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                        <span>{p.plan}</span>
                        <strong>{p.count} tenants</strong>
                      </div>
                    ))}
                  </div>
                  <div style={S.card}>
                    <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Recent Sign-ups</h3>
                    {(data.overview.recent_signups || []).map((t, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                        <span>{t.name}</span>
                        <span style={{ color: 'var(--text-3)' }}>{new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* TENANTS */}
            {tab === 'Tenants' && (
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600 }}>All Tenants ({data.tenants?.total || 0})</h3>
                  <button onClick={() => setModal('tenant')} className="btn btn-primary" style={{ fontSize: 12 }}>＋ Onboard Tenant</button>
                </div>
                <table style={S.tbl}>
                  <thead><tr>
                    {['Name','Plan','Status','Members','Created','Actions'].map(h => <th key={h} style={S.th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {(data.tenants?.tenants || []).map(t => (
                      <tr key={t.id}>
                        <td style={S.td}><strong>{t.name}</strong><br /><span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t.slug}</span></td>
                        <td style={S.td}>{t.plan_name || 'Free'}</td>
                        <td style={S.td}><span style={{ color: statusColor(t.status), fontWeight: 600 }}>{t.status}</span></td>
                        <td style={S.td}>{t.member_count}</td>
                        <td style={S.td}>{new Date(t.created_at).toLocaleDateString()}</td>
                        <td style={S.td}>
                          {t.status === 'suspended'
                            ? <button onClick={() => activateTenant(t.id)} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: '#a6e3a1' }}>Activate</button>
                            : <button onClick={() => suspendTenant(t.id)} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: '#f38ba8' }}>Suspend</button>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* USERS */}
            {tab === 'Users' && (
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600 }}>All Users ({data.users?.total || 0})</h3>
                  <button onClick={() => setModal('user')} className="btn btn-primary" style={{ fontSize: 12 }}>＋ Create User</button>
                </div>
                <table style={S.tbl}>
                  <thead><tr>{['Name','Email','Role','Status','Joined','Actions'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(data.users?.users || []).map(u => {
                      const inactive = u.status === 'inactive';
                      return (
                        <tr key={u.id} style={inactive ? { opacity: 0.55 } : undefined}>
                          <td style={S.td}>{u.name}</td>
                          <td style={S.td}>{u.email}</td>
                          <td style={S.td}><code style={{ fontSize: 11, background: 'var(--bg)', padding: '2px 6px', borderRadius: 4 }}>{u.role}</code></td>
                          <td style={S.td}>
                            <span style={{ fontWeight: 600, color: inactive ? '#f38ba8' : '#a6e3a1' }}>
                              {inactive ? 'inactive' : 'active'}
                            </span>
                          </td>
                          <td style={S.td}>{new Date(u.created_at).toLocaleDateString()}</td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => openEditUser(u)} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}>Edit</button>
                              <button onClick={() => toggleUserStatus(u)} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: inactive ? '#a6e3a1' : '#f38ba8' }}>
                                {inactive ? 'Activate' : 'Deactivate'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* PLANS */}
            {tab === 'Plans' && (
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 600 }}>Subscription Plans ({(data.plans || []).length})</h3>
                  <button onClick={openCreatePlan} className="btn btn-primary" style={{ fontSize: 12 }}>＋ Create Plan</button>
                </div>
                <table style={S.tbl}>
                  <thead><tr>{['Plan','Monthly','Yearly','Members','Projects','API Keys','Calls/mo','Status','Actions'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(data.plans || []).map(p => {
                      const lim = (n) => n === -1 ? '∞' : Number(n).toLocaleString();
                      return (
                        <tr key={p.id} style={!p.is_active ? { opacity: 0.55 } : undefined}>
                          <td style={S.td}><strong>{p.name}</strong><br /><span style={{ fontSize: 11, color: 'var(--text-3)' }}>{p.slug}</span></td>
                          <td style={S.td}>${p.price_monthly}</td>
                          <td style={S.td}>${p.price_yearly}</td>
                          <td style={S.td}>{lim(p.max_members)}</td>
                          <td style={S.td}>{lim(p.max_projects)}</td>
                          <td style={S.td}>{lim(p.max_api_keys)}</td>
                          <td style={S.td}>{lim(p.max_monthly_calls)}</td>
                          <td style={S.td}><span style={{ fontWeight: 600, color: p.is_active ? '#a6e3a1' : '#f38ba8' }}>{p.is_active ? 'active' : 'inactive'}</span></td>
                          <td style={S.td}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => openEditPlan(p)} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px' }}>Edit</button>
                              <button onClick={() => togglePlan(p)} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: p.is_active ? '#fab387' : '#a6e3a1' }}>{p.is_active ? 'Deactivate' : 'Activate'}</button>
                              <button onClick={() => deletePlan(p)} className="btn btn-ghost" style={{ fontSize: 11, padding: '3px 8px', color: '#f38ba8' }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* FEATURE FLAGS */}
            {tab === 'Feature Flags' && (
              <div style={S.card}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Platform Feature Flags</h3>
                <table style={S.tbl}>
                  <thead><tr>{['Key','Name','Default','Plan Required'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(data.flags || []).map(f => (
                      <tr key={f.id}>
                        <td style={S.td}><code style={{ fontSize: 11 }}>{f.key_name}</code></td>
                        <td style={S.td}>{f.name}</td>
                        <td style={S.td}><span style={{ color: f.default_enabled ? '#a6e3a1' : '#f38ba8' }}>{f.default_enabled ? '✓ Enabled' : '✗ Disabled'}</span></td>
                        <td style={S.td}>{f.plan_required || <span style={{ color: 'var(--text-3)' }}>All plans</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* AUDIT LOGS */}
            {tab === 'Audit Logs' && (
              <div style={S.card}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Audit Logs</h3>
                <table style={S.tbl}>
                  <thead><tr>{['Action','Actor','Resource','Tenant','When'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {(data.logs?.logs || []).map(l => (
                      <tr key={l.id}>
                        <td style={S.td}><code style={{ fontSize: 11 }}>{l.action}</code></td>
                        <td style={S.td}>{l.actor_name || '—'}</td>
                        <td style={S.td}>{l.resource_type || '—'}</td>
                        <td style={S.td}>{l.tenant_id?.slice(0,8) || 'Platform'}</td>
                        <td style={S.td}>{new Date(l.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* SETTINGS */}
            {tab === 'Settings' && data.settings && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {Object.entries(data.settings).map(([k, v]) => (
                  <div key={k} style={S.stat}>
                    <div style={S.statLbl}>{k.replace(/_/g, ' ')}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{String(v)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* One-time temp-credential reveal (new tenant owner) */}
      {createdNote && (
        <div style={MODAL.overlay} onClick={() => setCreatedNote(null)}>
          <div style={MODAL.box} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: '#a6e3a1' }}>✓ Tenant onboarded — owner credentials (shown once)</h3>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Email: <code style={MODAL.code}>{createdNote.email}</code></div>
            <div style={{ fontSize: 13 }}>Temp password: <code style={MODAL.code}>{createdNote.temp_password}</code></div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>The owner must set a new password on first login.</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => { navigator.clipboard.writeText(`Email: ${createdNote.email}\nTemp password: ${createdNote.temp_password}`); toast.success('Copied!'); }}>Copy</button>
              <button className="btn btn-primary" onClick={() => setCreatedNote(null)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Create User modal */}
      {modal === 'user' && (
        <div style={MODAL.overlay} onClick={() => setModal(null)}>
          <div style={MODAL.box} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Create Platform User</h3>
            <input style={MODAL.input} placeholder="Full name *" value={userForm.name} onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} />
            <input style={MODAL.input} placeholder="Email *" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} />
            <input style={MODAL.input} type="password" placeholder="Password (min 8) *" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} />
            <select style={MODAL.input} value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}>
              {['admin','member','viewer','agent','client'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={createUser}>Create user</button>
            </div>
          </div>
        </div>
      )}

      {/* Onboard Tenant modal */}
      {modal === 'tenant' && (
        <div style={MODAL.overlay} onClick={() => setModal(null)}>
          <div style={MODAL.box} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Onboard Tenant</h3>
            <p style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 16 }}>Creates a company with a Business Admin owner. If the owner email is new, an account is created with a temporary password.</p>
            <input style={MODAL.input} placeholder="Company name *" value={tenantForm.name} onChange={e => setTenantForm(f => ({ ...f, name: e.target.value }))} />
            <input style={MODAL.input} placeholder="Owner email *" value={tenantForm.owner_email} onChange={e => setTenantForm(f => ({ ...f, owner_email: e.target.value }))} />
            <input style={MODAL.input} placeholder="Owner full name (required if new)" value={tenantForm.owner_name} onChange={e => setTenantForm(f => ({ ...f, owner_name: e.target.value }))} />
            <select style={MODAL.input} value={tenantForm.plan_id} onChange={e => setTenantForm(f => ({ ...f, plan_id: e.target.value }))}>
              {(plans.length ? plans : [{ id: 'plan_free', name: 'Free' }]).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={createTenant}>Onboard</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User modal */}
      {modal === 'editUser' && editUser && (
        <div style={MODAL.overlay} onClick={() => setModal(null)}>
          <div style={MODAL.box} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Edit User</h3>
            <label style={MODAL.label}>Name</label>
            <input style={MODAL.input} value={editUser.name} onChange={e => setEditUser(u => ({ ...u, name: e.target.value }))} />
            <label style={MODAL.label}>Role</label>
            <select style={MODAL.input} value={editUser.role} onChange={e => setEditUser(u => ({ ...u, role: e.target.value }))}>
              {GLOBAL_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveUser}>Save changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Plan create/edit modal */}
      {modal === 'plan' && (
        <div style={MODAL.overlay} onClick={() => setModal(null)}>
          <div style={{ ...MODAL.box, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>{editingPlanId ? 'Edit Plan' : 'Create Plan'}</h3>
            <label style={MODAL.label}>Plan name</label>
            <input style={MODAL.input} placeholder="Professional" value={planForm.name} onChange={e => setPlanForm(f => ({ ...f, name: e.target.value }))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={MODAL.label}>Price / month ($)</label><input style={MODAL.input} type="number" value={planForm.price_monthly} onChange={e => setPlanForm(f => ({ ...f, price_monthly: e.target.value }))} /></div>
              <div><label style={MODAL.label}>Price / year ($)</label><input style={MODAL.input} type="number" value={planForm.price_yearly} onChange={e => setPlanForm(f => ({ ...f, price_yearly: e.target.value }))} /></div>
              <div><label style={MODAL.label}>Max members (-1 = ∞)</label><input style={MODAL.input} type="number" value={planForm.max_members} onChange={e => setPlanForm(f => ({ ...f, max_members: e.target.value }))} /></div>
              <div><label style={MODAL.label}>Max projects</label><input style={MODAL.input} type="number" value={planForm.max_projects} onChange={e => setPlanForm(f => ({ ...f, max_projects: e.target.value }))} /></div>
              <div><label style={MODAL.label}>Max API keys</label><input style={MODAL.input} type="number" value={planForm.max_api_keys} onChange={e => setPlanForm(f => ({ ...f, max_api_keys: e.target.value }))} /></div>
              <div><label style={MODAL.label}>Max calls / month</label><input style={MODAL.input} type="number" value={planForm.max_monthly_calls} onChange={e => setPlanForm(f => ({ ...f, max_monthly_calls: e.target.value }))} /></div>
            </div>
            <label style={MODAL.label}>Features (comma-separated keys)</label>
            <input style={MODAL.input} placeholder="api_testing, mock_server, analytics" value={(planForm.features || []).join(', ')}
              onChange={e => setPlanForm(f => ({ ...f, features: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={savePlan}>{editingPlanId ? 'Save plan' : 'Create plan'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MODAL = {
  overlay: { position: 'fixed', inset: 0, background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 },
  box:     { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420 },
  input:   { width: '100%', marginBottom: 10, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' },
  label:   { display: 'block', fontSize: 12, color: 'var(--text-2)', marginBottom: 4 },
  code:    { background: 'var(--bg)', padding: '2px 8px', borderRadius: 6, fontFamily: 'monospace' },
};
